import React, { useEffect, useRef, useState } from 'react'
import { FloatingBubble } from 'antd-mobile'
import { history } from 'umi'

import aiChatbotEntry from '../../assets/image/ai_chatbot_entry.png'
import goTo from '../../libs/functions/goTo'
import { getStorage } from '../../libs/functions/onStorage'
import { AiChatEntryResponse, getAiChatEntry } from '../../services'

const H5_TOKEN_STORAGE_KEY = 'h5Token'
const CHATBOT_ROUTE = '/chatbot/room'
const LOGIN_ROUTE = '/login'
const UNAUTHENTICATED_SYNC_INTERVAL = 2000
const AUTHENTICATED_SYNC_INTERVAL = 60 * 1000

type EntryStatus = 'checking' | 'enabled' | 'stopped'

const hasH5Token = () => Boolean(getStorage(H5_TOKEN_STORAGE_KEY))

const shouldShowBubble = () => {
  const pathname = history.location.pathname
  const isChatbotRoute =
    pathname === CHATBOT_ROUTE || pathname.startsWith(`${CHATBOT_ROUTE}/`)

  const isLoginRoute =
    pathname === LOGIN_ROUTE || pathname.startsWith(`${LOGIN_ROUTE}/`)

  return hasH5Token() && !isChatbotRoute && !isLoginRoute
}

const bubbleStyle = {
  '--edge-distance': '15px',
  '--initial-position-bottom': (screen.height / 3.5 || 22).toFixed(0) + 'px',
  '--initial-position-right': '15px',
  '--z-index': '1000',
  '--size': '56px',
  '--background': 'transparent',
}

const imageStyle = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain' as const,
}

export default function AiChatbotBubble() {
  const [entryConfig, setEntryConfig] = useState<AiChatEntryResponse | null>(
    null,
  )
  const [visible, setVisible] = useState(false)
  const [entryStatus, setEntryStatus] = useState<EntryStatus>('checking')
  const hasRequestedEntryConfig = useRef(false)

  useEffect(() => {
    if (entryStatus === 'stopped') {
      setVisible(false)
      return
    }

    let isUnmounted = false
    let timer: number | undefined

    const getSyncInterval = () =>
      entryStatus === 'enabled' && hasH5Token()
        ? AUTHENTICATED_SYNC_INTERVAL
        : UNAUTHENTICATED_SYNC_INTERVAL

    const loadEntryConfig = () => {
      if (hasRequestedEntryConfig.current || !hasH5Token()) return

      hasRequestedEntryConfig.current = true
      getAiChatEntry()
        .then((entryConfig) => {
          if (isUnmounted) return
          if (entryConfig?.enabled === true) {
            setEntryConfig(entryConfig)
            setVisible(shouldShowBubble())
            setEntryStatus('enabled')
            return
          }
          setEntryStatus('stopped')
        })
        .catch(() => {
          if (!isUnmounted) {
            setEntryStatus('stopped')
          }
        })
    }

    const syncVisible = () => {
      if (!hasH5Token()) {
        setVisible(false)
        return
      }
      if (entryStatus === 'checking') {
        loadEntryConfig()
        return
      }
      setVisible(shouldShowBubble())
    }
    const scheduleSync = () => {
      if (timer !== undefined) {
        window.clearTimeout(timer)
      }
      timer = window.setTimeout(() => {
        syncVisible()
        scheduleSync()
      }, getSyncInterval())
    }
    const syncVisibleAndSchedule = () => {
      syncVisible()
      scheduleSync()
    }
    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key === H5_TOKEN_STORAGE_KEY) {
        syncVisibleAndSchedule()
      }
    }

    const unlisten = history.listen(syncVisibleAndSchedule)
    syncVisible()
    scheduleSync()
    window.addEventListener('storage', handleStorage)

    return () => {
      unlisten()
      if (timer !== undefined) {
        window.clearTimeout(timer)
      }
      window.removeEventListener('storage', handleStorage)
    }
  }, [entryStatus])

  if (entryStatus !== 'enabled' || !visible) return null

  return (
    <FloatingBubble
      axis="xy"
      magnetic="x"
      style={bubbleStyle}
      aria-label="AI Chatbot"
      onClick={() => goTo('/chatbot/room')}
    >
      <img
        src={entryConfig?.iconUrl || aiChatbotEntry}
        alt=""
        aria-hidden="true"
        style={imageStyle}
      />
    </FloatingBubble>
  )
}
